import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { yupResolver } from '@hookform/resolvers/dist/ie11/yup/yup';
import Confirm from 'hoc/modal/Confirm';
import * as yup from 'yup';
import { Alert, Message, Input, Button } from 'components/atoms';
import { SocialLoginButtons } from 'components/blocks';
import Footer from 'components/templates/layout/Footer';
import { postAuthSignup, postAuthCheckEmail } from 'utils/api';
import useModal from 'hooks/useModal';
import { Modal } from 'hoc/modal';
import logo from 'assets/images/logo.png';
import { menu } from 'utils/constants';

const schema = yup.object({
  name: yup.string().required('이름을 입력해주세요.'),
  email: yup
    .string()
    .email('이메일 형식이 올바르지 않습니다.')
    .required('이메일을 입력해주세요.'),
});

const Signup = () => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
  } = useForm({ resolver: yupResolver(schema) });
  const navigate = useNavigate();
  const {
    show: showConfirm,
    openModal: openConfirm,
    closeModal: closeConfirm,
  } = useModal({ autoClose: false });
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const goToLogin = () => {
    navigate(menu.login.link);
  };

  const checkEmailDuplicate = async (email) => {
    try {
      const res = await postAuthCheckEmail({ email });
      if (res.statusCode === 200 && res.results?.isDuplicate) {
        setError('email', {
          type: 'manual',
          message: '이미 가입된 이메일입니다.',
        });
        return false;
      }
      return true;
    } catch (e) {
      return true;
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    setErrorMessage('');

    // 이메일 중복 체크
    const isEmailAvailable = await checkEmailDuplicate(data.email);
    if (!isEmailAvailable) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await postAuthSignup({
        name: data.name,
        email: data.email,
      });
      if (res.statusCode === 200) {
        openConfirm();
      } else {
        setErrorMessage(res.message || '회원가입 요청에 실패했습니다.');
      }
    } catch (e) {
      const message =
        e.response?.data?.message || '회원가입 요청에 실패했습니다.';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Logo src={logo} />
          <Title>
            <span
              style={{
                fontWeight: 300,
              }}
            >
              &nbsp;&nbsp;&nbsp;급매 탐색기
            </span>
          </Title>
        </div>
        <SubTitle>회원가입</SubTitle>
        <Contents>이름과 이메일을 입력하시면</Contents>
        <Contents style={{ marginBottom: 20 }}>
          비밀번호 설정 안내 메일을 보내드립니다.
        </Contents>

        <InputWrapper>
          <Label>
            이름<Required>*</Required>
          </Label>
          <Input
            name="name"
            placeholder="이름을 입력해주세요."
            ref={register}
            variant={errors.name && 'danger'}
            style={{
              fontSize: 12,
              fontWeight: 400,
              cursor: 'pointer',
            }}
          />
          {errors.name && (
            <Message variant="danger">{errors.name.message}</Message>
          )}
        </InputWrapper>

        <InputWrapper>
          <Label>
            이메일<Required>*</Required>
          </Label>
          <Input
            name="email"
            type="email"
            placeholder="이메일을 입력해주세요."
            ref={register}
            variant={errors.email && 'danger'}
            style={{
              fontSize: 12,
              fontWeight: 400,
              cursor: 'pointer',
            }}
          />
          {errors.email && (
            <Message variant="danger">{errors.email.message}</Message>
          )}
        </InputWrapper>

        <div style={{ margin: '10px 0 30px' }}>
          {errorMessage !== '' && (
            <Alert variant="danger">{errorMessage}</Alert>
          )}
        </div>

        <Button disabled={isLoading}>
          {isLoading ? '처리 중...' : '회원가입'}
        </Button>
        <div style={{ height: 10 }} />
        <Button
          type="button"
          onClick={goToLogin}
          style={{ backgroundColor: '#fff', color: '#8800ff' }}
        >
          로그인으로 돌아가기
        </Button>

        <SocialLoginButtons />

        <Modal show={showConfirm}>
          <Confirm
            onClose={closeConfirm}
            success="확인"
            onOk={goToLogin}
            type="check"
            title="이메일 발송 완료"
          >
            회원가입 안내 이메일이 입력하신 주소로 전송되었습니다.
            <br />
            이메일에서 비밀번호 설정 버튼을 클릭하여 가입을 완료해주세요.
            <br />
            <br />
            만약, 메일을 받지 못하셨다면 '스팸 메일함'을 확인해주세요.
          </Confirm>
        </Modal>
      </Form>
      <Footer />
    </Container>
  );
};

const Container = styled.div`
  width: 30rem;
  margin: auto;
  padding-top: 3rem;
  line-height: 1.5;
  font-size: 0.8rem;

  input {
    height: 50px;
    padding-left: 10px;
    letter-spacing: -0.03rem;
  }

  button {
    height: 50px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 12.8;

    &:hover {
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 40px;
  background-color: #fff;
  padding: 60px 40px;
  border-radius: 21px;
  box-shadow: 5px 5px 5px #eee;
`;

const Title = styled.div`
  font-size: 2rem;
  font-weight: bold;
  text-align: center;
  display: flex;
  align-items: center;
`;

const SubTitle = styled.div`
  margin-top: -15px;
  margin-bottom: 36px;
  font-weight: 500;
  text-align: center;
  font-size: 14.5px;
  line-height: 22px;
`;

const Contents = styled.div`
  margin: 0 auto;
  font-size: 12px;
  font-weight: 500;
`;

const InputWrapper = styled.div`
  margin-top: 15px;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  letter-spacing: -0.03rem;
`;

const Required = styled.span`
  color: #8800ff;
  font-weight: 500;
  margin-left: 2px;
`;

const Logo = styled.img`
  width: 150px;
`;

export default Signup;
